<template>
  <div v-if="!capacityExceededWarningConfig.show">
    <DateAndPeople
      ref="dateAndPeople"
      class="mb-4"
      :title-visible="false"
      @track="track"
      @input="onFormInteraction"
    />

    <Captcha :config="$store.state.captcha" @onTokenResponse="(token) => { $store.commit('setCaptchaToken', token); }" />

    <RestrictionsInfo
      v-if="shouldShowRestrictions"
      class="mb-4"
      :restrictions="restrictions"
    />

    <EnquiryPricingTable />

    <BaseButton
      :is-loading="$store.state.isLoading.sendEnquiry"
      color="red"
      size="large"
      class="flex justify-center items-center mb-4 w-full"
      data-test-id="enquiry-form__date-and-ppl-btn"
      @click="submit"
    >
      {{ $store.getters.cta.firstStep }}
    </BaseButton>

    <div
      v-if="$store.getters.isRtbFlowPossible"
      class="flex justify-center items-center mt-2 text-grey-600 text-xs"
    >
      You won't be charged yet
    </div>
  </div>

  <CapacityExceededWarning
    v-else
    class="mt-6"
    :people="capacityExceededWarningConfig.people"
    @back="capacityExceededWarningConfig.show = false"
    @continue="enquireAnyway"
  />
</template>

<script>
import { mapState } from 'vuex';

import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import RestrictionsInfo from '@/components/Ui/RestrictionsInfo.vue';
import CapacityExceededWarning from '@/modules/public/enquiry-form/components/EnquiryForm/components/CapacityExceeded/CapacityExceededWarning.vue';
import Captcha from '@/modules/public/enquiry-form/components/EnquiryForm/components/Captcha.vue';
import DateAndPeople from '@/modules/public/enquiry-form/components/EnquiryForm/forms/DateAndPeople.vue';
import EnquiryPricingTable from '@/modules/public/enquiry-form/components/EnquiryPricingTable.vue';
import { setContextParam } from '@/services/context-store.js';

export default {
  name: 'StepDateAndPeople',
  emits: ['submit'],
  components: {
    Captcha,
    EnquiryPricingTable,
    BaseButton,
    RestrictionsInfo,
    CapacityExceededWarning,
    DateAndPeople,
  },

  data() {
    return {
      capacityExceededWarningConfig: {
        show: false,
        people: null,
      },
      hasUserInteracted: false,
    };
  },

  computed: {
    ...mapState([
      'form',
      'pages',
      'room',
      'companyId',
    ]),
    shouldShowRestrictions() {
      const isTargetCompany = this.companyId === 2798;
      const hasRestrictions = this.restrictions.length > 0;
      return isTargetCompany && hasRestrictions && this.hasUserInteracted;
    },
    restrictions() {
      return [
        'Promoted / ticketed events are not allowed',
        'Guests under 18 are not allowed after 20:00',
      ];
    },
    priceTotalFormatted() {
      return this.$store.state.pricing?.paymentDetails?.grossPrice?.formatted || null;
    },
  },

  watch: {
    priceTotalFormatted(newVal, oldVal) {
      if (!oldVal && newVal) {
        this.$store.dispatch('trackGA4Interaction', {
          section: 'event_info',
          element: 'price_calculation',
          interaction: 'visible',
          value: newVal,
        });
      }
    },
  },

  methods: {
    onFormInteraction() {
      if (!this.hasUserInteracted) {
        this.hasUserInteracted = true;
      }
    },
    submit() {
      this.trackSubmitBtnClicked();

      const { isValid, data } = this.$refs.dateAndPeople.submit();
      if (!isValid) {
        return;
      }

      const people = parseInt(data.people.value, 10);
      const isOverPeopleLimit = people > this.room.capacity.max;
      if (isOverPeopleLimit) {
        this.capacityExceededWarningConfig = {
          show: true,
          people,
        };
      } else {
        this.finishSubmit();
      }

      const element = this.$store.getters.isRtbFlowPossible ? 'request_to_book_button' : 'enquire_now_button';
      this.$store.dispatch('trackGA4Interaction', {
        section: 'event_info',
        element,
        interaction: 'click',
      });
    },
    trackSubmitBtnClicked() {
      setContextParam('UserCabinet', 'room_page');

      const label = this.$store.getters.isRtbFlowPossible ? 'enquire-now-request-to-book' : 'enquire-now';
      this.$store.dispatch('trackEnquiryFormNavigation', {
        label,
      });
    },
    enquireAnyway() {
      this.$store.dispatch('trackEnquiryFormNavigation', {
        label: 'enquire-anyway',
      });
      this.finishSubmit();
    },

    finishSubmit() {
      this.$emit('submit', {
        requestToBookPath: true,
      });
    },
    track({ element, interaction, value = null }) {
      const actionName = 'trackGA4Interaction';
      const section = 'event_info';

      this.$store.dispatch(actionName, {
        section,
        element,
        interaction,
        value,
      });
    },
  },
};
</script>
